import React, { useState, useEffect } from 'react';
import { useDataProvider, useNotify, useRefresh, useRecordContext } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { CloudDownload as SyncIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { apiClient } from '../../framework/api/api.client';

/**
 * Location Config Sync Component
 * 
 * Allows syncing location metadata, devices, and applications from parent Neo instance
 */
export const LocationConfigSync: React.FC = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  
  const [connections, setConnections] = useState<any[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<string>('');
  const [discovering, setDiscovering] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [discoveryResult, setDiscoveryResult] = useState<any>(null);
  
  // Sync options
  const [syncLocation, setSyncLocation] = useState(true);
  const [syncDevices, setSyncDevices] = useState(true);
  const [syncApplications, setSyncApplications] = useState(false);
  const [syncDashboards, setSyncDashboards] = useState(false);
  const [syncDataModels, setSyncDataModels] = useState(false);
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [selectedDashboards, setSelectedDashboards] = useState<string[]>([]);
  const [selectedDataModels, setSelectedDataModels] = useState<string[]>([]);
  
  useEffect(() => {
    loadConnections();
  }, []);
  
  const loadConnections = async () => {
    try {
      const { data } = await dataProvider.getList('connection', {
        filter: {},
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' }
      });
      
      // Filter for Neo Platform connections using correct field name
      const neoConnections = data.filter((conn: any) => 
        conn.connectionProviderKey === 'neo-platform'
      );
      
      setConnections(neoConnections);
      if (neoConnections.length > 0) {
        setSelectedConnection(neoConnections[0]._id);
      }
    } catch (error) {
      console.error('Failed to load connections:', error);
    }
  };
  
  const handleDiscover = async () => {
    if (!selectedConnection) {
      notify('Please select a parent connection', { type: 'warning' });
      return;
    }
    
    setDiscovering(true);
    setDiscoveryResult(null);
    
    try {
      // Backend now returns dashboards in the discover response
      const result = await apiClient.call(`/sync/discover-location-config?connectionId=${selectedConnection}`);
      
      setDiscoveryResult(result);
      
      const parts = [
        `${result.summary.deviceCount} devices`,
        `${result.summary.applicationCount} applications`,
        `${result.summary.dashboardCount || 0} dashboards`,
        `${result.summary.dataModelCount || 0} data models`
      ];
      
      notify(`Discovered location config: ${parts.join(', ')}`, { type: 'success' });
    } catch (error: any) {
      console.error('Failed to discover config:', error);
      notify(error.message || 'Failed to discover configuration from parent', { type: 'error' });
    } finally {
      setDiscovering(false);
    }
  };
  
  const handleSync = async () => {
    if (!selectedConnection) {
      notify('Please select a parent connection', { type: 'warning' });
      return;
    }
    
    if (!discoveryResult) {
      notify('Please discover configuration first', { type: 'warning' });
      return;
    }
    
    setSyncing(true);
    
    try {
      const result = await apiClient.call('/sync/sync-location-config', {
        method: 'POST',
        body: {
          connectionId: selectedConnection,
          syncLocation,
          syncDevices,
          syncApplications,
          syncDashboards,
          syncDataModels,
          applicationIds: selectedApps,
          dashboardIds: selectedDashboards,
          dataModelIds: selectedDataModels
        }
      });
      
      const parts = [
        result.location ? `Location: ${result.location.action}` : null,
        result.devices.length > 0 ? `Devices: ${result.devices.length}` : null,
        result.applications.length > 0 ? `Apps: ${result.applications.length}` : null,
        result.dashboards && result.dashboards.length > 0 ? `Dashboards: ${result.dashboards.length}` : null,
        result.dataModels && result.dataModels.length > 0 ? `Data Models: ${result.dataModels.length}` : null,
      ].filter(Boolean);
      
      notify(`✅ Sync complete! ${parts.join(', ')}`, { type: 'success' });
      
      // Refresh the page to show updated data
      refresh();
    } catch (error: any) {
      console.error('Failed to sync config:', error);
      notify(error.message || 'Failed to sync configuration from parent', { type: 'error' });
    } finally {
      setSyncing(false);
    }
  };
  
  const handleAppToggle = (appId: string) => {
    setSelectedApps(prev => 
      prev.includes(appId)
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };
  
  const handleDashboardToggle = (dashboardId: string) => {
    setSelectedDashboards(prev => 
      prev.includes(dashboardId)
        ? prev.filter(id => id !== dashboardId)
        : [...prev, dashboardId]
    );
  };
  
  const handleDataModelToggle = (dataModelId: string) => {
    setSelectedDataModels(prev => 
      prev.includes(dataModelId)
        ? prev.filter(id => id !== dataModelId)
        : [...prev, dataModelId]
    );
  };
  
  if (!record?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          Save the location first to sync configuration
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Sync from Parent Platform
      </Typography>
      
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Pull location metadata, devices, and applications from the parent environment
      </Typography>
      
      {/* Connection Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl fullWidth>
            <InputLabel>Parent Connection</InputLabel>
            <Select
              value={selectedConnection}
              onChange={(e) => setSelectedConnection(e.target.value)}
              label="Parent Connection"
            >
              {connections.length === 0 && (
                <MenuItem value="" disabled>
                  No Neo Platform connections found
                </MenuItem>
              )}
              {connections.map((conn: any) => (
                <MenuItem key={conn._id} value={conn._id}>
                  {conn.name} ({conn.config?.instanceUrl})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              onClick={handleDiscover}
              disabled={!selectedConnection || discovering}
              variant="outlined"
              startIcon={discovering ? <CircularProgress size={20} /> : <RefreshIcon />}
            >
              {discovering ? 'Discovering...' : 'Discover Configuration'}
            </Button>
          </Box>
        </CardContent>
      </Card>
      
      {/* Discovery Results */}
      {discoveryResult && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Available to Sync
            </Typography>
            
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell><strong>Count</strong></TableCell>
                    <TableCell><strong>Sync?</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Location Metadata</TableCell>
                    <TableCell>
                      <Chip label={discoveryResult.location?.name || 'Not found'} size="small" color="primary" />
                    </TableCell>
                    <TableCell>
                      <Checkbox 
                        checked={syncLocation} 
                        onChange={(e) => setSyncLocation(e.target.checked)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Devices</TableCell>
                    <TableCell>
                      <Chip label={discoveryResult.devices?.length || 0} size="small" />
                    </TableCell>
                    <TableCell>
                      <Checkbox 
                        checked={syncDevices} 
                        onChange={(e) => setSyncDevices(e.target.checked)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Applications</TableCell>
                    <TableCell>
                      <Chip label={discoveryResult.applications?.length || 0} size="small" />
                    </TableCell>
                    <TableCell>
                      <Checkbox 
                        checked={syncApplications} 
                        onChange={(e) => setSyncApplications(e.target.checked)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Dashboards</TableCell>
                    <TableCell>
                      <Chip label={discoveryResult.dashboards?.length || 0} size="small" />
                    </TableCell>
                    <TableCell>
                      <Checkbox 
                        checked={syncDashboards} 
                        onChange={(e) => setSyncDashboards(e.target.checked)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Data Models</TableCell>
                    <TableCell>
                      <Chip label={discoveryResult.dataModels?.length || 0} size="small" />
                    </TableCell>
                    <TableCell>
                      <Checkbox 
                        checked={syncDataModels} 
                        onChange={(e) => setSyncDataModels(e.target.checked)}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Application Selection (if sync applications is checked) */}
            {syncApplications && discoveryResult.applications && discoveryResult.applications.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Select Applications to Import:
                </Typography>
                <FormGroup>
                  {discoveryResult.applications.map((app: any) => (
                    <FormControlLabel
                      key={app.id}
                      control={
                        <Checkbox 
                          checked={selectedApps.includes(app.id)}
                          onChange={() => handleAppToggle(app.id)}
                        />
                      }
                      label={`${app.name} (v${app.currentVersion})`}
                    />
                  ))}
                </FormGroup>
              </Box>
            )}
            
            {/* Dashboard Selection (if sync dashboards is checked) */}
            {syncDashboards && discoveryResult.dashboards && discoveryResult.dashboards.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Select Dashboards to Import:
                </Typography>
                <FormGroup>
                  {discoveryResult.dashboards.map((dashboard: any) => (
                    <FormControlLabel
                      key={dashboard.id}
                      control={
                        <Checkbox 
                          checked={selectedDashboards.includes(dashboard.id)}
                          onChange={() => handleDashboardToggle(dashboard.id)}
                        />
                      }
                      label={`${dashboard.name} (v${dashboard.currentVersion || 1})`}
                    />
                  ))}
                </FormGroup>
              </Box>
            )}
            
            {/* Data Model Selection (if sync data models is checked) */}
            {syncDataModels && discoveryResult.dataModels && discoveryResult.dataModels.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Select Data Models to Import:
                </Typography>
                <FormGroup>
                  {discoveryResult.dataModels.map((dataModel: any) => (
                    <FormControlLabel
                      key={dataModel.id}
                      control={
                        <Checkbox 
                          checked={selectedDataModels.includes(dataModel.id)}
                          onChange={() => handleDataModelToggle(dataModel.id)}
                        />
                      }
                      label={`${dataModel.name} (${dataModel.scope}, v${dataModel.currentVersion || 1})`}
                    />
                  ))}
                </FormGroup>
              </Box>
            )}
            
            {/* Sync Button */}
            <Box sx={{ mt: 3 }}>
              <Button
                onClick={handleSync}
                disabled={syncing || (!syncLocation && !syncDevices && !syncApplications && !syncDashboards && !syncDataModels)}
                variant="contained"
                color="primary"
                startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                fullWidth
              >
                {syncing ? 'Syncing...' : 'Sync from Parent'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      
      {/* Help Text */}
      {!discoveryResult && (
        <Alert severity="info">
          <Typography variant="body2">
            <strong>How it works:</strong>
          </Typography>
          <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0 }}>
            <li>Select your parent Neo Platform connection</li>
            <li>Click "Discover Configuration" to see what's available</li>
            <li>Choose what to sync (location metadata, devices, applications, dashboards)</li>
            <li>Click "Sync from Parent" to import the selected items</li>
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <strong>Note:</strong> Location metadata will be merged (name, description, timezone, address, contact).
            Tenant and Brand relationships are managed by the environment and cannot be changed via sync.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

